import { AdminIcon } from "@minket/brand/icons/admin";
import { AutomationsIcon } from "@minket/brand/icons/automations";
import { BroadcastsIcon } from "@minket/brand/icons/broadcasts";
import { ContactsIcon } from "@minket/brand/icons/contacts";
import { DomainsIcon } from "@minket/brand/icons/domains";
import { EmailsIcon } from "@minket/brand/icons/emails";
import type { IconComponent } from "@minket/brand/icons/icon";
import { KeysIcon } from "@minket/brand/icons/keys";
import { OverviewIcon } from "@minket/brand/icons/overview";
import { SettingsIcon } from "@minket/brand/icons/settings";
import { TemplatesIcon } from "@minket/brand/icons/templates";
import type { FileRouteTypes } from "../routeTree.gen";

/** Every path the router knows; a typo in a list below fails the typecheck. */
export type RoutePath = FileRouteTypes["to"];

/**
 * Every page of the console, defined once. The rail shows nine sections with their icons; each
 * section's pages are tabs in its page head (components/page/section-tabs.tsx); the ⌘K palette
 * offers every tab and every setup step directly (lib/palette.ts, with each page's icon and the
 * words it is found by, which only the palette needs). Each `to` is typed as a path of the
 * generated route tree, so a page's worker builds the page file and never edits navigation. URLs
 * stay where they are: a section gathers pages, it does not move them (`/settings/smtp` is a
 * Developers tab). Detail pages (`/emails/$id`, ...) and the setup flow (lib/setup.ts) are not
 * tabs; they belong to the section whose `holds` covers them, and the rail marks that section.
 */

export interface PageEntry {
  to: RoutePath;
  label: string;
}

export interface Section {
  key: string;
  /** The rail's word, and the page head's title on each of its tabs. */
  label: string;
  icon: IconComponent;
  /** Its pages, in the order their tabs show; the first is where the rail goes. */
  tabs: readonly [PageEntry, ...PageEntry[]];
  /** Paths it holds that are not tabs, as prefixes: detail pages, the setup flow. */
  holds?: readonly string[];
  /** Shown only to platform administrators. */
  adminOnly?: boolean;
}

/** The rail, top to bottom. */
export const sections = [
  {
    key: "overview",
    label: "Overview",
    icon: OverviewIcon,
    tabs: [
      { to: "/", label: "Overview" },
      { to: "/alerts", label: "Alerts" },
      { to: "/status", label: "Status" },
    ],
    holds: ["/onboarding"],
  },
  {
    key: "emails",
    label: "Emails",
    icon: EmailsIcon,
    tabs: [
      { to: "/emails", label: "Sent" },
      { to: "/inbound", label: "Received" },
      { to: "/sandbox", label: "Test mode" },
      { to: "/suppressions", label: "Suppressions" },
    ],
    holds: ["/emails/", "/inbound/"],
  },
  {
    key: "templates",
    label: "Templates",
    icon: TemplatesIcon,
    tabs: [{ to: "/templates", label: "Templates" }],
    holds: ["/templates/"],
  },
  {
    key: "broadcasts",
    label: "Broadcasts",
    icon: BroadcastsIcon,
    tabs: [{ to: "/broadcasts", label: "Broadcasts" }],
    holds: ["/broadcasts/"],
  },
  {
    key: "automations",
    label: "Automations",
    icon: AutomationsIcon,
    tabs: [{ to: "/automations", label: "Automations" }],
    holds: ["/automations/"],
  },
  {
    key: "audience",
    label: "Audience",
    icon: ContactsIcon,
    tabs: [
      { to: "/contacts", label: "Contacts" },
      { to: "/segments", label: "Segments" },
      { to: "/topics", label: "Topics" },
    ],
    holds: ["/contacts/"],
  },
  {
    key: "domains",
    label: "Domains",
    icon: DomainsIcon,
    tabs: [
      { to: "/domains", label: "Domains" },
      { to: "/mailboxes", label: "Mailboxes" },
    ],
    holds: ["/domains/"],
  },
  {
    key: "developers",
    label: "Developers",
    icon: KeysIcon,
    tabs: [
      { to: "/keys", label: "API keys" },
      { to: "/webhooks", label: "Webhooks" },
      { to: "/logs", label: "Logs" },
      { to: "/settings/smtp", label: "SMTP" },
    ],
  },
  {
    key: "settings",
    label: "Settings",
    icon: SettingsIcon,
    tabs: [
      { to: "/settings/organization", label: "Organization" },
      { to: "/settings/projects", label: "Projects" },
      { to: "/settings/sessions", label: "Sessions" },
      { to: "/team", label: "Team" },
      { to: "/audit", label: "Audit log" },
      { to: "/settings/cloudflare", label: "Cloudflare" },
      { to: "/settings/oauth", label: "OAuth clients" },
    ],
    holds: ["/settings"],
  },
  {
    key: "admin",
    label: "Admin",
    icon: AdminIcon,
    tabs: [
      { to: "/admin", label: "Overview" },
      { to: "/admin/organizations", label: "Organizations" },
      { to: "/admin/users", label: "Users" },
      { to: "/admin/health", label: "Health" },
      { to: "/admin/usage", label: "Usage" },
      { to: "/admin/audit", label: "Audit" },
      { to: "/admin/support", label: "Support" },
      { to: "/admin/settings", label: "Settings" },
    ],
    holds: ["/admin/organizations/", "/admin/users/", "/admin/support/"],
    adminOnly: true,
  },
] as const satisfies readonly Section[];

/** The sections this viewer may see: platform administrators alone see the Admin group. */
export function sectionsFor(platformAdmin: boolean): readonly Section[] {
  return platformAdmin
    ? sections
    : (sections as readonly Section[]).filter((section) => section.adminOnly !== true);
}

/** Every tab of every section: the pages the rail and the page heads reach. */
export const tabPages: readonly PageEntry[] = sections.flatMap(
  (section): readonly PageEntry[] => section.tabs,
);

function normalize(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
}

function holds(prefix: string, path: string): boolean {
  if (prefix.endsWith("/")) return path.startsWith(prefix);
  return path === prefix || path.startsWith(`${prefix}/`);
}

/** The section a path belongs to: the one whose tab it is, else the one that holds it. */
export function sectionFor(pathname: string): Section | null {
  const path = normalize(pathname);
  const byTab = sections.find((section) => section.tabs.some((tab) => tab.to === path));
  if (byTab) return byTab;
  let found: Section | null = null;
  let longest = -1;
  for (const section of sections as readonly Section[]) {
    for (const prefix of section.holds ?? []) {
      if (holds(prefix, path) && prefix.length > longest) {
        found = section;
        longest = prefix.length;
      }
    }
  }
  return found;
}

/** The tab a path is, with its section; null on detail pages and the setup flow. */
export function tabFor(pathname: string): { section: Section; tab: PageEntry } | null {
  const path = normalize(pathname);
  for (const section of sections as readonly Section[]) {
    const tab = section.tabs.find((entry) => entry.to === path);
    if (tab) return { section, tab };
  }
  return null;
}

/** The title a tab page gives the document: "Received · Emails", or "Templates" alone. */
export function tabTitle(section: Section, tab: PageEntry): string {
  return tab.label === section.label ? tab.label : `${tab.label} · ${section.label}`;
}
